/**
 * Pipeline-specific isomorphic types for the client scrub pass and the API
 * contracts that drive it.
 *
 * These are kept separate from the domain primitives in ./types (catalog,
 * inference, validation) because they describe the *transport/scrub* surface —
 * the upload format, the user-confirmed scrub plan, and the local PII signals —
 * not the modeling domain. Per the IMPLEMENTATION_PLAN Conflict D & E rulings,
 * `UploadFormat` is defined ONCE here, and `FileType`/`InferredType` are imported
 * from ./types (never redefined).
 *
 * Pure and isomorphic: no DOM, no node:*, no runtime dependencies.
 */

import type { FileType } from './types';

/** The two upload file formats the browser pipeline accepts. */
export const UPLOAD_FORMATS = ['csv', 'xlsx'] as const;
export type UploadFormat = (typeof UPLOAD_FORMATS)[number];

/**
 * A header as read during preflight, with its column position fixed for the
 * whole pass. `index` (not `name`) is the identity that crosses the
 * preflight→scrub boundary: headers can repeat or contain commas, but the
 * 0-based position is unambiguous and stable across the two reads of the file.
 */
export interface ColumnHeader {
  /** 0-based column position — the join key between preflight and scrub. */
  readonly index: number;
  /** Raw header text, trimmed. */
  readonly name: string;
}

/**
 * Local (non-AI) PII verdict for a column, derived from the bounded value
 * sample by {@link classifyColumnPii}. Discriminated on `kind`; the `none`
 * variant carries no ratio so an empty/clean column can't be confused with a
 * zero-ratio match.
 */
export type LocalPiiSignal =
  | { readonly kind: 'none' }
  | { readonly kind: 'email'; readonly matchRatio: number }
  | { readonly kind: 'phone'; readonly matchRatio: number }
  | { readonly kind: 'ssn'; readonly matchRatio: number };

/**
 * The user-confirmed plan that drives the scrub pass (built by the PII-preflight
 * UI). Identifies every column by index; the scrub pass refuses to run without a
 * Loan ID column (PRODUCT.md "PII Preflight": Loan ID is required).
 */
export interface ScrubPlan {
  readonly fileType: FileType;
  readonly format: UploadFormat;
  /**
   * Column index of Loan ID, or null when this group has no Loan ID (an
   * origination/transaction extract from a different system may lack one). Scrub
   * requires AT LEAST ONE join key — a Loan ID OR a Tax ID — never neither.
   */
  readonly loanIdColumnIndex: number | null;
  /** Column index of Tax ID, or null when the user did not select one. */
  readonly taxIdColumnIndex: number | null;
  /**
   * Column indexes the user marked PII → dropped before upload. Excludes the
   * Loan ID / Tax ID columns (those are HMAC'd, not dropped).
   */
  readonly droppedColumnIndexes: ReadonlySet<number>;
  /** All headers, by index. Used to build the scrubbed output header row. */
  readonly headers: readonly ColumnHeader[];
}
